// MUI components
import { useEffect, useState, useContext } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { Button, Box, Grid, Typography } from '@mui/material'
import {
  format,
  subMonths,
  subYears,
  differenceInMonths,
  differenceInYears,
} from 'date-fns'

const OnboardingMilitaryServiceDates = ({
  preHeader,
  header,
  subHeader,
  helpText,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [serviceTimeQuestion, setServiceTimeQuestion] = useState(false)
  const [yearsEntered, setYearsEntered] = useState(0)
  const [monthsEntered, setMonthsEntered] = useState(0)

  const dateToYears = intakeState.militaryActiveDutyStartDate
    ? differenceInYears(
        new Date(),
        new Date(intakeState.militaryActiveDutyStartDate)
      )
    : ''

  const dateToMonths = intakeState.militaryActiveDutyStartDate
    ? differenceInMonths(
        new Date(),
        new Date(intakeState.militaryActiveDutyStartDate)
      ) -
      (dateToYears > 0 ? dateToYears : yearsEntered) * 12
    : ''

  const yearsToDate = (years) => {
    return format(subYears(new Date(), years), 'yyyy-MM-dd')
  }
  const monthsToDate = (months) => {
    const totalTime =
      Number(dateToYears > 0 ? dateToYears : yearsEntered) * 12 + Number(months)
    return format(subMonths(new Date(), totalTime), 'yyyy-MM-dd')
  }

  useEffect(() => {
    if (intakeState.militaryActiveDutyStartDate) {
      const diffYears = differenceInYears(
        new Date(),
        new Date(intakeState.militaryActiveDutyStartDate)
      )
      const diffMonths =
        differenceInMonths(
          new Date(),
          new Date(intakeState.militaryActiveDutyStartDate)
        ) -
        diffYears * 12

      setYearsEntered(diffYears)
      setMonthsEntered(diffMonths)
    }
  }, [intakeState.militaryActiveDutyStartDate])

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box>
        <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
          {preHeader || ''}
        </Typography>
      </Box>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          {header || "Let's start with your basic information:"}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ lineHeight: '1.2' }}
        fontSize={{ xs: '16px', sm: '20px' }}
      >
        {subHeader ||
          'Let us know your name and other basic details about you!'}
      </Typography>
      <Box component="form" py={3}>
        <Grid container spacing={1}>
          {serviceTimeQuestion ? (
            <>
              <Grid item xs={12}>
                <Box mt={3}>
                  <Typography variant="body1Medium">
                    Active Duty Start Date
                  </Typography>
                </Box>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryActiveDutyStartDate',
                  label: <>{/* <span>Active Duty Start Date</span> */}</>,
                  placeholder: 'mm/dd/yyyy',
                  enableHelpText: true,
                  helpText: helpText,
                  inputType: 'splitdate',
                  value: intakeState.militaryActiveDutyStartDate,
                  onChange: (militaryActiveDutyStartDate) =>
                    updateIntakeStateAtKey({ militaryActiveDutyStartDate }),
                })}
              </Grid>
              <Grid item xs={12} mb={1}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => {
                      setServiceTimeQuestion(false)
                      setYearsEntered(0)
                      setMonthsEntered(0)
                    }}
                  >
                    Enter Years and Months Served Instead
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body1Medium">
                    Active Duty Discharge Date
                  </Typography>
                </Box>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryActiveDutyEndDate',
                  label: <>{/* <span>Active Duty Discharge Date</span> */}</>,
                  placeholder: 'mm/dd/yyyy',
                  inputType: 'splitdate',
                  enableHelpText: true,
                  helpText: helpText,
                  value: intakeState.militaryActiveDutyEndDate,
                  onChange: (militaryActiveDutyEndDate) =>
                    updateIntakeStateAtKey({ militaryActiveDutyEndDate }),
                })}
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="body1Medium" mt={1} mb={1}>
                  Total Time Served
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {renderMuiFormField({
                  label: (
                    <>
                      <span>Years Served</span>
                    </>
                  ),
                  inputType: 'int',
                  placeholder: 'Ex: 5',
                  value: dateToYears,
                  onChange: (e) => {
                    const newYears = Number(e)
                    setYearsEntered(newYears)

                    const totalMonths = newYears * 12 + Number(monthsEntered)
                    updateIntakeStateAtKey({
                      militaryActiveDutyEndDate: format(
                        new Date(),
                        'yyyy-MM-dd'
                      ),
                      militaryActiveDutyStartDate: format(
                        subMonths(new Date(), totalMonths),
                        'yyyy-MM-dd'
                      ),
                    })
                  },
                })}
              </Grid>

              <Grid item xs={6}>
                {renderMuiFormField({
                  label: (
                    <>
                      <span>Months Served</span>
                    </>
                  ),
                  inputType: 'int',
                  id: 'ActiveDutyMonths',
                  placeholder: 'Ex: 3',
                  value: dateToMonths,

                  onChange: (e) => {
                    const newMonths = Number(e)
                    setMonthsEntered(newMonths)

                    const totalMonths = Number(yearsEntered) * 12 + newMonths
                    updateIntakeStateAtKey({
                      militaryActiveDutyEndDate: format(
                        new Date(),
                        'yyyy-MM-dd'
                      ),
                      militaryActiveDutyStartDate: format(
                        subMonths(new Date(), totalMonths),
                        'yyyy-MM-dd'
                      ),
                    })
                  },
                })}
              </Grid>
              <Grid item xs={12} mb={1}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => setServiceTimeQuestion(true)}
                  >
                    Enter Service Dates Instead
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  )
}

export default OnboardingMilitaryServiceDates
